import { Divider, Flex } from '@mantine/core';
import { ChatState, Message, MessageDirection, ProposedAction } from '../chat/types';
import { VerifyOutcome } from '../chat/verify/types';
import { ChatFileCardRow } from './ChatFileCardRow';
import { PresentedFileCard } from './PresentedFileCard';
import { SiestaMessage } from './SiestaMessage';
import { TemplateProposalWidget } from './TemplateProposalWidget';
import { ToolLimitReachedWidget } from './ToolLimitReachedWidget';
import { UserMessage } from './UserMessage';

type Props = {
  messages: Message[];
  latestIncomingId?: string;
  isStreaming?: boolean;
  onContinue: (widgetMessageId: string) => void;
  onProposalDecision?: (
    widgetMessageId: string | null,
    proposal: ProposedAction,
    approve: boolean,
  ) => void;
  // Pending proposals recovered from the server after a reload, when no in-memory message
  // carries the widget any more.
  orphanProposals?: ProposedAction[];
  isDecidingProposal?: boolean;
  onVerifyAction?: (outcome: VerifyOutcome, note?: string) => void;
  dark?: boolean;
};

export function SiestaBarMessages({
  messages,
  latestIncomingId,
  isStreaming,
  onContinue,
  onProposalDecision,
  orphanProposals,
  isDecidingProposal,
  onVerifyAction,
  dark,
}: Props) {
  const renderProposals = (message: Extract<Message, { kind: 'message' }>) =>
    message.proposedActions?.map(proposal => (
      <TemplateProposalWidget
        key={proposal.proposalId}
        proposal={proposal}
        onDecide={approve => onProposalDecision?.(message.id, proposal, approve)}
        disabled={isStreaming || isDecidingProposal}
      />
    ));

  return (
    <Flex direction="column" gap="md" px="md" py="md">
      {messages.map(message => {
        if (message.kind === 'interrupted') {
          return (
            <Divider
              key={message.id}
              label="Interrupted"
              labelPosition="center"
              c="dimmed"
              my="xs"
            />
          );
        }
        if (message.direction === MessageDirection.Outgoing) {
          return (
            <Flex key={message.id} direction="column" gap="xs">
              {message.attachments?.length ? <ChatFileCardRow files={message.attachments} /> : null}
              <UserMessage content={message.answerContent || message.response[0]?.content || ''} />
            </Flex>
          );
        }

        if (message.currentState === ChatState.TOOL_LIMIT_REACHED) {
          const hasPrePauseContent = Boolean(
            message.answerContent.trim() || message.thinkingContent.trim(),
          );
          return (
            <Flex key={message.id} direction="column" gap="md">
              {hasPrePauseContent && (
                <SiestaMessage
                  content={message.answerContent}
                  thinkingContent={message.thinkingContent}
                  dark={dark}
                />
              )}
              {message.presentedFiles?.map(file => (
                <PresentedFileCard key={file.url} file={file} />
              ))}
              {renderProposals(message)}
              <ToolLimitReachedWidget
                onContinue={() => onContinue(message.id)}
                disabled={isStreaming}
              />
            </Flex>
          );
        }

        const isLatest = message.id === latestIncomingId;
        const isToolInFlight = Boolean(message.currentToolName);
        const isThinking =
          isLatest &&
          isStreaming &&
          (isToolInFlight ||
            (message.currentState !== 'ANSWERING' &&
              message.currentState !== 'SENT' &&
              message.currentState !== ''));

        return (
          <Flex key={message.id} direction="column" gap="md">
            <SiestaMessage
              content={message.answerContent}
              thinkingContent={message.thinkingContent}
              isThinking={isThinking}
              currentState={message.currentState}
              currentToolName={isThinking ? message.currentToolName : undefined}
              retryNotice={isLatest && isStreaming ? message.retryNotice : undefined}
              verifyOutcome={message.verifyOutcome}
              evidence={message.evidence}
              verifyMeetingNoteId={message.verifyMeetingNoteId}
              verifyNote={message.verifyNote}
              onVerifyAction={onVerifyAction}
              dark={dark}
            />
            {message.presentedFiles?.map(file => (
              <PresentedFileCard key={file.url} file={file} />
            ))}
            {renderProposals(message)}
          </Flex>
        );
      })}
      {orphanProposals?.map(proposal => (
        <TemplateProposalWidget
          key={proposal.proposalId}
          proposal={proposal}
          onDecide={approve => onProposalDecision?.(null, proposal, approve)}
          disabled={isStreaming || isDecidingProposal}
        />
      ))}
    </Flex>
  );
}
